import {
  Component, Input, Output, EventEmitter, OnChanges, OnDestroy, HostListener, SimpleChanges
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-banner-slider',
  templateUrl: './banner-slider.component.html',
  styleUrls: ['./banner-slider.component.scss']
})
export class BannerSliderComponent implements OnChanges, OnDestroy {

  @Input() slides: SafeHtml[] = [];
  @Input() currentIndex: number = 0;
  /** Must match the CSS transition duration (default 350ms). */
  @Input() transitionMs: number = 350;

  @Output() prevClick = new EventEmitter<void>();
  @Output() nextClick = new EventEmitter<void>();
  @Output() dotClick = new EventEmitter<number>();
  @Output() dragStart = new EventEmitter<void>();
  @Output() dragEnd = new EventEmitter<void>();

  displayIndex = 0;
  isDragging = false;
  noTransition = false;

  private dragStartX = 0;
  private dragOffset = 0;
  private wrapTimer: any = null;
  private pendingExternalSkips = 0;

  // Track layout for N≥2: [clone-of-last, slide0, slide1, …, slideN-1, clone-of-first]
  // Real index i lives at DOM slot i+1.
  get displaySlides(): SafeHtml[] {
    const s = this.slides;
    if (!s || s.length <= 1) { return s || []; }
    return [s[s.length - 1]].concat(s).concat([s[0]]);
  }

  get trackOffset(): string {
    const pct = (-this.displayIndex * 100).toFixed(4);
    if (this.dragOffset !== 0) {
      return 'translateX(calc(' + pct + '% + ' + this.dragOffset + 'px))';
    }
    return 'translateX(' + pct + '%)';
  }

  get trackTransition(): string {
    if (this.isDragging || this.noTransition) { return 'none'; }
    return 'transform ' + this.transitionMs + 'ms ease';
  }

  // ── DOM slot helpers ─────────────────────────────────────────────────────────

  private toDisplayIndex(idx: number): number {
    if (!this.slides || this.slides.length <= 1) { return 0; }
    return idx + 1;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['currentIndex']) {
      if (changes['slides']) {
        // Slides replaced externally — re-sync display position.
        this.displayIndex = this.toDisplayIndex(this.currentIndex);
      }
      return;
    }

    const curr: number = changes['currentIndex'].currentValue;

    if (changes['currentIndex'].firstChange) {
      this.displayIndex = this.toDisplayIndex(curr);
      return;
    }

    if (this.pendingExternalSkips > 0) {
      this.pendingExternalSkips--;
      return;
    }

    if (this.wrapTimer !== null) { return; }

    const prev: number = changes['currentIndex'].previousValue;
    const total = this.slides ? this.slides.length : 0;

    // Only forward-wrap is detected here (auto-play always goes forward).
    // Backward wrap is triggered exclusively from onPrevClick / commitDrag.
    if (total > 1 && prev === total - 1 && curr === 0) {
      this.applyForwardWrap(total);
    } else {
      this.displayIndex = this.toDisplayIndex(curr);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.wrapTimer);
  }

  // ── Button handlers ──────────────────────────────────────────────────────────

  onPrevClick(): void {
    if (this.wrapTimer !== null) { return; }
    const total = this.slides ? this.slides.length : 1;
    if (total > 1 && this.currentIndex === 0) {
      this.applyBackwardWrap();
    } else {
      this.displayIndex = this.toDisplayIndex((this.currentIndex - 1 + total) % total);
    }
    this.pendingExternalSkips++;
    this.prevClick.emit();
  }

  onNextClick(): void {
    if (this.wrapTimer !== null) { return; }
    const total = this.slides ? this.slides.length : 1;
    if (total > 1 && this.currentIndex === total - 1) {
      this.applyForwardWrap(total);
    } else {
      this.displayIndex = this.toDisplayIndex(this.currentIndex + 1);
    }
    this.pendingExternalSkips++;
    this.nextClick.emit();
  }

  onDotClick(index: number): void {
    if (this.wrapTimer !== null) { return; }
    this.displayIndex = this.toDisplayIndex(index);
    this.pendingExternalSkips++;
    this.dotClick.emit(index);
  }

  // ── Mouse drag ───────────────────────────────────────────────────────────────

  onViewportMouseDown(e: MouseEvent): void {
    e.preventDefault();
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragOffset = 0;
    this.dragStart.emit();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(e: MouseEvent): void {
    if (!this.isDragging) { return; }
    this.dragOffset = e.clientX - this.dragStartX;
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (!this.isDragging) { return; }
    this.isDragging = false;
    this.commitDrag();
    this.dragEnd.emit();
  }

  // ── Touch drag ───────────────────────────────────────────────────────────────
  // touch-action: pan-y on the viewport (CSS) handles vertical-scroll conflicts.

  onViewportTouchStart(e: TouchEvent): void {
    this.isDragging = true;
    this.dragStartX = e.touches[0].clientX;
    this.dragOffset = 0;
    this.dragStart.emit();
  }

  onViewportTouchMove(e: TouchEvent): void {
    if (!this.isDragging) { return; }
    this.dragOffset = e.touches[0].clientX - this.dragStartX;
  }

  onViewportTouchEnd(): void {
    if (!this.isDragging) { return; }
    this.isDragging = false;
    this.commitDrag();
    this.dragEnd.emit();
  }

  onViewportTouchCancel(): void {
    if (!this.isDragging) { return; }
    this.isDragging = false;
    this.dragOffset = 0;
    this.dragEnd.emit();
  }

  // ── Commit / wrap helpers ────────────────────────────────────────────────────

  private commitDrag(): void {
    const THRESHOLD = 50;
    const offset = this.dragOffset;
    const total = this.slides ? this.slides.length : 1;

    if (offset < -THRESHOLD) {
      if (total > 1 && this.currentIndex === total - 1) {
        this.applyForwardWrap(total);
      } else {
        this.displayIndex = this.toDisplayIndex(this.currentIndex + 1);
      }
      this.dragOffset = 0;
      this.pendingExternalSkips++;
      this.nextClick.emit();
    } else if (offset > THRESHOLD) {
      if (total > 1 && this.currentIndex === 0) {
        this.applyBackwardWrap();
      } else {
        this.displayIndex = this.toDisplayIndex((this.currentIndex - 1 + total) % total);
      }
      this.dragOffset = 0;
      this.pendingExternalSkips++;
      this.prevClick.emit();
    } else {
      this.dragOffset = 0;
    }
  }

  private applyForwardWrap(total: number): void {
    this.displayIndex = total + 1; // animate to clone-of-first at end
    clearTimeout(this.wrapTimer);
    this.wrapTimer = setTimeout(() => {
      this.noTransition = true;
      this.displayIndex = this.toDisplayIndex(this.currentIndex);
      setTimeout(() => {
        this.noTransition = false;
        this.wrapTimer = null;
      });
    }, this.transitionMs);
  }

  private applyBackwardWrap(): void {
    this.displayIndex = 0; // animate to clone-of-last at start
    clearTimeout(this.wrapTimer);
    this.wrapTimer = setTimeout(() => {
      this.noTransition = true;
      this.displayIndex = this.toDisplayIndex(this.currentIndex);
      setTimeout(() => {
        this.noTransition = false;
        this.wrapTimer = null;
      });
    }, this.transitionMs);
  }
}
